import React, { useMemo } from "react";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals as theme, themevals } from "theme/themevals";
import { ProvidersIcon, ProductsIcon } from "components/svg";
import P from "components/typography/P";
import TableBasic from "components/ui/Table";
import { createColumnHelper } from "@tanstack/react-table";

type OverviewProps = {
  dispatch?: any;
};

const Overview: React.FC<OverviewProps> = ({ dispatch }) => {
  const columnHelper = createColumnHelper<any>();

  const renderAddress = () => {
    const address = dispatch?.project?.location?.address;
    if (!address) {
      return "-";
    }

    const { line1, line2, city, state, postalCode } = address;
    const firstLineParts = [line1, line2].filter(Boolean) as string[];
    const firstLine = firstLineParts.length > 1 ? `${firstLineParts[0]}, ${firstLineParts[1]}` : firstLineParts[0] ?? "";

    const statePostal = [state, postalCode].filter(Boolean).join(" ").trim();
    const secondLine = city && statePostal ? `${city}, ${statePostal}` : city || statePostal;

    const lines = [firstLine, secondLine].filter(Boolean);

    if (!lines.length) {
      return "-";
    }

    return lines.map((line, idx) => (
      <React.Fragment key={idx}>
        {line}
        {idx < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const mapUrl = (() => {
    const latitude = dispatch?.project?.location?.latitude;
    const longitude = dispatch?.project?.location?.longitude;
    if (typeof latitude !== "number" || typeof longitude !== "number") {
      return null;
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
  })();

  const productQuantityLabel = (() => {
    const productItem = dispatch?.purchaseOrder?.lineitems?.find((item: any) => item?.type === "product");
    if (!productItem) {
      return null;
    }

    const quantity = Number(productItem.quantity);
    const quantityText = Number.isFinite(quantity) ? String(quantity) : "";
    const unit = typeof productItem.unit === "string" ? productItem.unit.trim() : "";

    const parts = [quantityText, unit].filter(Boolean);
    return parts.length ? parts.join(" ") : null;
  })();

  const productName = (() => {
    const productItem = dispatch?.purchaseOrder?.lineitems?.find((item: any) => item?.type === "product");
    const name = typeof productItem?.name === "string" ? productItem.name.trim() : "";
    return name || null;
  })();

  const serviceName = (() => {
    const serviceItem = dispatch?.purchaseOrder?.lineitems?.find((item: any) => item?.type === "service");
    const name = typeof serviceItem?.name === "string" ? serviceItem.name.trim() : "";
    return name || null;
  })();

  const addonProducts = useMemo(() => {
    if (!Array.isArray(dispatch?.purchaseOrder?.lineitems)) {
      return [];
    }

    return dispatch.purchaseOrder.lineitems.filter((item: any) => item?.type === "addon-product");
  }, [dispatch]);

  const addonColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => <span>Add-on Product</span>,
        cell: (info) => info.getValue() || "-",
        meta: {
          style: { width: "50%", minWidth: "50%", maxWidth: "50%" },
        },
      }),
      columnHelper.accessor("quantity", {
        header: () => <Box sx={{ width: "100%", textAlign: "right" }}>Quantity</Box>,
        cell: (info) => {
          const value = info.getValue();
          const unit = info.row.original?.unit;
          const quantityText = Number.isFinite(Number(value)) ? String(value) : "";
          const unitText = unit ? String(unit).trim() : "";
          const parts = [quantityText, unitText].filter(Boolean);
          return parts.length ? parts.join(" ") : "-";
        },
        meta: {
          style: {
            width: "50%",
            minWidth: "50%",
            maxWidth: "50%",
            textAlign: "right",
          },
        },
      }),
    ],
    [columnHelper]
  );

  return (
    <>
      <Flex
        sx={{
          flex: 1,
          borderRadius: "6px",
          border: `1px solid ${themevals.colors.mdgrey}`,
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          flexDirection: "column",
          marginTop: "20px",
          background: "#fff",
        }}
      >
        <Flex sx={{ padding: "20px", borderBottom: `1px solid ${themevals.colors.mdgrey}`, flexDirection: "row", alignItems: "center" }}>
          <Flex sx={{ flex: 1, alignItems: "center" }}>
            <Flex sx={{ marginRight: "8px" }}>
              <ProvidersIcon width="18px" fill={themevals.colors.black} />
            </Flex>
            <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>Dispatch Details</Flex>
          </Flex>
          <Flex sx={{ flex: 1, justifyContent: "flex-end", alignItems: "center" }}>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"0px"} marginBottom={"0px"}>
              {(() => {
                switch (dispatch?.status) {
                  case 0:
                    return (
                      <Badge as="span" variant="warning">
                        Created
                      </Badge>
                    );
                  case 1:
                    return (
                      <Badge as="span" variant="success">
                        Dispatched
                      </Badge>
                    );
                  case 3:
                    return (
                      <Badge as="span" variant="info">
                        On Hold
                      </Badge>
                    );
                  case 4:
                    return (
                      <Badge as="span" variant="success">
                        On Site
                      </Badge>
                    );
                  case 5:
                    return (
                      <Badge as="span" variant="success">
                        Complete
                      </Badge>
                    );
                  default:
                    return (
                      <Badge as="span" variant="warning">
                        Created
                      </Badge>
                    );
                }
              })()}
            </P>
          </Flex>
        </Flex>
        <Flex sx={{ flexDirection: "column", padding: "30px 20px" }}>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Product</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {productName || "-"}
            </P>
          </Flex>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Name</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {dispatch?.project?.siteContactName ? dispatch?.project?.siteContactName : "-"}
            </P>
          </Flex>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Phone</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {dispatch?.project?.siteContactPhone ? dispatch?.project?.siteContactPhone : "-"}
            </P>
          </Flex>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Email</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {dispatch?.project?.siteContactEmail ? dispatch?.project?.siteContactEmail : "-"}
            </P>
          </Flex>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Address</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {mapUrl ? (
                <Link
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: "underline",
                    color: themevals.colors.black,
                    "&:hover": { color: themevals.colors.black },
                    "&:visited": { color: themevals.colors.black },
                    "&:active": { color: themevals.colors.black },
                  }}
                >
                  {renderAddress()}
                </Link>
              ) : (
                renderAddress()
              )}
            </P>
          </Flex>
        </Flex>
      </Flex>
      <Flex
        sx={{
          flex: 1,
          borderRadius: "6px",
          border: `1px solid ${themevals.colors.mdgrey}`,
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          flexDirection: "column",
          marginTop: "20px",
          background: "#fff",
        }}
      >
        <Flex sx={{ padding: "20px", borderBottom: `1px solid ${themevals.colors.mdgrey}`, flexDirection: "row", alignItems: "center" }}>
          <Flex sx={{ flex: 2, alignItems: "center" }}>
            <Flex sx={{ marginRight: "8px" }}>
              <ProductsIcon width="18px" fill={themevals.colors.black} />
            </Flex>
            <Flex sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600 }}>Products & Services</Flex>
          </Flex>
          <Flex sx={{ flex: 1, justifyContent: "flex-end", alignItems: "center" }}>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"0px"} marginBottom={"0px"}>
              <Badge as="span" variant="ltgrey">
                {productQuantityLabel || "-"}
              </Badge>
            </P>
          </Flex>
        </Flex>
        <Flex sx={{ flexDirection: "column", padding: "30px 20px" }}>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Bulk Product</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {productName || "-"} - {productQuantityLabel || "-"}
            </P>
          </Flex>
          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Service</Label>
            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
              {serviceName || "-"}
            </P>
          </Flex>
          {addonProducts.length > 0 && (
            <Flex sx={{ flexDirection: "column", marginBottom: "0px" }}>
              <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey, marginBottom: "8px" }}>Add-on Products</Label>
              <Flex>
                <TableBasic
                  data={addonProducts}
                  columns={addonColumns}
                  enablePaging={false}
                  enableSearch={false}
                  customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
                  disableSorting
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default Overview;
